import React, {useMemo, useState, useCallback} from 'react'
import {FixedSizeList as List} from 'react-window'

// npm install react-window
// npm install -D @types/react-window

interface Props<T> {
  options: T[]
  selected: string[]
  onChange: (selected: string[]) => void
  // Tell the component how to get a unique string id and a display label
  // out of whatever shape T actually is (e.g. o => o.value, o => o.sender).
  getOptionValue: (option: T) => string
  getOptionLabel?: (option: T) => string
  height?: number
  itemHeight?: number
}

const LIST_HEIGHT = 360
const ROW_HEIGHT = 32

/**
 * Drop-in replacement for react-dual-listbox that only renders visible rows.
 * react-dual-listbox mounts every option as a real DOM node in both panes,
 * which is what freezes the browser at 30k+ items. This component keeps
 * the DOM node count constant (roughly height/itemHeight) no matter how
 * large `options` is.
 *
 * Generic over T so it doesn't assume a field name like `sender` or `value` —
 * you tell it via getOptionValue/getOptionLabel.
 */
export function VirtualizedDualListBox<T>({
  options,
  selected,
  onChange,
  getOptionValue,
  getOptionLabel,
  height = LIST_HEIGHT,
  itemHeight = ROW_HEIGHT,
}: Props<T>) {
  const getLabel = getOptionLabel ?? getOptionValue
  const [availableSearch, setAvailableSearch] = useState('')
  const [selectedSearch, setSelectedSearch] = useState('')

  const selectedSet = useMemo(() => new Set(selected), [selected])

  const availableOptions = useMemo(
    () => options.filter((o) => !selectedSet.has(getOptionValue(o))),
    [options, selectedSet, getOptionValue]
  )

  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(getOptionValue(o))),
    [options, selectedSet, getOptionValue]
  )

  const filteredAvailable = useMemo(() => {
    if (!availableSearch) return availableOptions
    const q = availableSearch.toLowerCase()
    return availableOptions.filter((o) => getLabel(o).toLowerCase().includes(q))
  }, [availableOptions, availableSearch, getLabel])

  const filteredSelected = useMemo(() => {
    if (!selectedSearch) return selectedOptions
    const q = selectedSearch.toLowerCase()
    return selectedOptions.filter((o) => getLabel(o).toLowerCase().includes(q))
  }, [selectedOptions, selectedSearch, getLabel])

  // Double-click a row to move it directly, no checkbox step needed
  const moveToSelected = useCallback(
    (value: string) => {
      if (selectedSet.has(value)) return
      onChange([...selected, value])
    },
    [selected, selectedSet, onChange]
  )

  const moveToAvailable = useCallback(
    (value: string) => {
      onChange(selected.filter((s) => s !== value))
    },
    [selected, onChange]
  )

  const moveAllRight = useCallback(() => {
    onChange([...selected, ...filteredAvailable.map(getOptionValue)])
  }, [selected, filteredAvailable, onChange, getOptionValue])

  const moveAllLeft = useCallback(() => {
    const removeSet = new Set(filteredSelected.map(getOptionValue))
    onChange(selected.filter((s) => !removeSet.has(s)))
  }, [selected, filteredSelected, onChange, getOptionValue])

  const AvailableRow = useCallback(
    ({index, style}: {index: number; style: React.CSSProperties}) => {
      const item = filteredAvailable[index]
      const value = getOptionValue(item)
      return (
        <div
          style={style}
          className='d-flex align-items-center px-6 dual-list-row'
          onDoubleClick={() => moveToSelected(value)}
          title='Double-click to move'
        >
          <span className='text-truncate'>{getLabel(item)}</span>
        </div>
      )
    },
    [filteredAvailable, getOptionValue, getLabel, moveToSelected]
  )

  const SelectedRow = useCallback(
    ({index, style}: {index: number; style: React.CSSProperties}) => {
      const item = filteredSelected[index]
      const value = getOptionValue(item)
      return (
        <div
          style={style}
          className='d-flex align-items-center px-6 dual-list-row'
          onDoubleClick={() => moveToAvailable(value)}
          title='Double-click to remove'
        >
          <span className='text-truncate'>{getLabel(item)}</span>
        </div>
      )
    },
    [filteredSelected, getOptionValue, getLabel, moveToAvailable]
  )

  return (
    <div className='d-flex align-items-start gap-3'>
      <style>{`
        .dual-list-row { cursor: pointer; user-select: none; }
        .dual-list-row:hover { background-color: rgba(0, 0, 0, 0.05); }
      `}</style>
      <div className='border border-gray-600 rounded flex-grow-1' style={{minWidth: 0}}>
        <div className='p-2 border-bottom'>
          <input
            type='text'
            className='form-control form-control-sm border border-gray-600'
            placeholder={`Search (${filteredAvailable.length})`}
            value={availableSearch}
            onChange={(e) => setAvailableSearch(e.target.value)}
          />
        </div>
        <List
          height={height}
          itemCount={filteredAvailable.length}
          itemSize={itemHeight}
          width='100%'
        >
          {AvailableRow}
        </List>
      </div>

      <div className='d-flex flex-column gap-2'>
        <button
          className='btn btn-sm btn-outline-primary'
          onClick={moveAllRight}
          title='Move all filtered'
        >
          &#8811;
        </button>
        <button
          className='btn btn-sm btn-outline-secondary'
          onClick={moveAllLeft}
          title='Remove all filtered'
        >
          &#8810;
        </button>
      </div>

      <div className='border border-gray-600 rounded flex-grow-1' style={{minWidth: 0}}>
        <div className='p-2 border-bottom'>
          <input
            type='text'
            className='form-control border border-gray-600 form-control-sm'
            placeholder={`Search (${filteredSelected.length})`}
            value={selectedSearch}
            onChange={(e) => setSelectedSearch(e.target.value)}
          />
        </div>
        <List
          height={height}
          itemCount={filteredSelected.length}
          itemSize={itemHeight}
          width='100%'
        >
          {SelectedRow}
        </List>
      </div>
    </div>
  )
}

export default VirtualizedDualListBox
